import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import {
  fetchCard,
  fetchCardLink,
  recordLinkClick,
  submitCardLinkForm,
  CardLink,
  Card,
  ContactFormFieldConfig,
} from "../utils/api";
import {
  ATTACHMENT_ACCEPT,
  CapturedAttachment,
  normalizeAttachmentDataUrl,
  readFileAsDataUrl,
  resolveAttachmentType,
  validateAttachmentFile,
} from "../utils/attachments";
import Skeleton from "./Skeleton";

// Dedicated "Custom Form" landing page. Routed from CardRenderer's contact-form
// link button so visitors get a focused, full-page form (no card-link distraction)
// and so card owners can preview/test the form from /my-card without the inline
// preview-mode block. Submission still posts to /api/cardlinks/:id/submit.

// ─── Form-field helper (mirrors what the inline form used to render) ─────────

interface CustomFieldProps {
  field: ContactFormFieldConfig;
  value: string | string[] | undefined;
  onChange: (v: string | string[]) => void;
  inputClass: string;
  labelClass: string;
  /**
   * file_upload only (D-005). The captured file can't travel through `onChange`
   * — that feeds `extra`, which becomes extraFields, whose values the server
   * caps at 1000 chars. So the page holds the attachment in its own state and
   * threads it through these three props instead.
   */
  onFileSelected?: (file: File | null) => void;
  attachmentName?: string;
  attachmentError?: string | null;
}

const CustomField: React.FC<CustomFieldProps> = ({
  field,
  value,
  onChange,
  inputClass,
  labelClass,
  onFileSelected,
  attachmentName,
  attachmentError,
}) => {
  const requiredMark = field.required ? <span className="text-accent-500"> *</span> : null;
  const stringValue = typeof value === "string" ? value : "";

  if (field.type === "file_upload") {
    const describedBy = `${field.id}-hint${attachmentError ? ` ${field.id}-error` : ""}`;
    return (
      <div>
        <label className={labelClass} htmlFor={field.id}>{field.label}{requiredMark}</label>
        <input
          id={field.id}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          aria-describedby={describedBy}
          aria-invalid={attachmentError ? true : undefined}
          onChange={(e) => onFileSelected?.(e.target.files?.[0] || null)}
          className="w-full text-sm text-coffee-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-coffee-100 file:text-coffee-800 hover:file:bg-coffee-200 file:cursor-pointer"
        />
        <p id={`${field.id}-hint`} className="mt-1 text-xs text-coffee-500">
          PDF or DOCX, up to 5MB.
        </p>
        {attachmentName && !attachmentError && (
          <p className="mt-1 text-xs text-coffee-700">
            Attached: <span className="font-medium">{attachmentName}</span>
          </p>
        )}
        {attachmentError && (
          <p id={`${field.id}-error`} role="alert" className="mt-1 text-xs text-danger-500">
            {attachmentError}
          </p>
        )}
      </div>
    );
  }

  if (field.type === "long_text") {
    return (
      <div>
        <label className={labelClass}>{field.label}{requiredMark}</label>
        <textarea
          required={field.required}
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClass} h-28 py-2 resize-y`}
        />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        <label className={labelClass}>{field.label}{requiredMark}</label>
        <select
          required={field.required}
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        >
          <option value="">Select…</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <label className={labelClass}>
          {field.label}{" "}
          <span className="text-coffee-500 font-normal">(select all that apply)</span>
        </label>
        <div className="space-y-1.5">
          {(field.options || []).map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={arr.includes(opt)}
                onChange={(e) => {
                  if (e.target.checked) onChange([...arr, opt]);
                  else onChange(arr.filter((o) => o !== opt));
                }}
              />
              {opt}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "yes_no") {
    return (
      <div>
        <label className={labelClass}>{field.label}{requiredMark}</label>
        <div className="flex gap-3">
          {["yes", "no"].map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer flex-1">
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={stringValue === opt}
                onChange={() => onChange(opt)}
                required={field.required}
              />
              <span className="capitalize">{opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  const inputType = field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text";
  return (
    <div>
      <label className={labelClass}>{field.label}{requiredMark}</label>
      <input
        type={inputType}
        required={field.required}
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

// Defined at module scope (not inside PublicCardForm) so its identity is stable
// across renders. When this lived inside the component, every keystroke produced
// a fresh function reference and React remounted the subtree, stealing focus
// from whichever input the user was typing in.
const Shell: React.FC<{ username?: string; children: React.ReactNode }> = ({ username, children }) => (
  <div className="min-h-screen bg-gradient-to-br from-coffee-50 to-cream-100">
    <div className="max-w-md mx-auto px-4 py-10">
      {username && (
        <Link
          to={`/card/${username}`}
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-coffee-600 hover:text-coffee-900 mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Back to card
        </Link>
      )}
      {children}
    </div>
  </div>
);

const PublicCardForm: React.FC = () => {
  const { username, linkId } = useParams<{ username: string; linkId: string }>();
  const [card, setCard] = useState<Card | null>(null);
  const [link, setLink] = useState<CardLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [extra, setExtra] = useState<Record<string, string | string[]>>({});
  // D-005: the file_upload field's file is held apart from `extra` so it never
  // ends up in extraFields (1000-char cap server-side).
  const [attachment, setAttachment] = useState<CapturedAttachment | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "submitting" | "sent" | "error">("idle");

  const handleFileSelected = async (file: File | null) => {
    const problem = validateAttachmentFile(file);
    if (problem || !file) {
      setAttachment(null);
      setAttachmentError(problem);
      return;
    }
    try {
      const raw = await readFileAsDataUrl(file);
      // Some browsers report an empty file.type for .docx. validateAttachmentFile
      // accepts those via the extension, so the effective type has to be
      // resolved HERE too — sending the empty string on would fail the server's
      // MIME allowlist and surface as a generic error after the visitor had
      // already filled in the whole form. The data URL's own prefix is
      // restamped to match, since readAsDataURL labels a typeless file
      // application/octet-stream and the server rejects that disagreement.
      const type = resolveAttachmentType(file);
      setAttachment({
        data: normalizeAttachmentDataUrl(raw, type),
        filename: file.name,
        type,
      });
      setAttachmentError(null);
    } catch {
      setAttachment(null);
      setAttachmentError("Could not read that file. Try again?");
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!username || !linkId) {
        setError("Missing card or form ID.");
        setLoading(false);
        return;
      }
      try {
        // Resolve by the link id, independent of which card is active. The
        // card context (name/theme/owner) comes from the card that OWNS the
        // link — not the handle's active card — so an owner can preview a form
        // on a non-active card and any valid form link opens. (BL-009)
        const linkData = await fetchCardLink(linkId);
        if (cancelled) return;
        if (linkData.linkType !== "contact_form") {
          setError("This link isn't a contact form.");
          setLoading(false);
          return;
        }
        const cardData = await fetchCard(linkData.cardId);
        if (cancelled) return;
        setCard(cardData);
        setLink(linkData);
        // Mirror the inline form's behavior: viewing the form counts as a click
        // on the link from the analytics side.
        recordLinkClick(linkId).catch(() => {});
      } catch {
        if (cancelled) return;
        setError("Form not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [username, linkId]);

  useEffect(() => {
    if (!card || !link) return;
    const buttonLabel = link.formConfig?.buttonLabel || link.title || "Contact me";
    const originalTitle = document.title;
    document.title = `${buttonLabel} – ${card.name} · WhoBrew`;
    return () => {
      document.title = originalTitle;
    };
  }, [card, link]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!link || state === "submitting") return;

    const customFields = link.formConfig?.customFields || [];

    // A required file field can't lean on the input's native `required`: once a
    // file is picked and then rejected by validation, the input still holds a
    // selection while our state is empty, so the browser would let it through.
    const fileField = customFields.find((f) => f.type === "file_upload");
    if (fileField?.required && !attachment) {
      setAttachmentError(attachmentError || "Choose a file to attach.");
      return;
    }

    setState("submitting");
    try {
      const cleanedExtra: Record<string, string | string[]> = {};
      for (const field of customFields) {
        // The attachment is sent top-level, never as an extraFields value.
        if (field.type === "file_upload") continue;
        const v = extra[field.id];
        if (v == null) continue;
        const isEmpty = Array.isArray(v) ? v.length === 0 : !v || String(v).trim() === "";
        if (isEmpty) continue;
        const key = field.label.slice(0, 64) || field.id;
        cleanedExtra[key] = v;
      }
      await submitCardLinkForm(link.id, {
        name,
        email,
        phone,
        extraFields: cleanedExtra,
        ...(attachment ? { attachment } : {}),
      });
      setState("sent");
    } catch {
      setState("error");
    }
  };

  if (loading) {
    return (
      <Shell username={username}>
        <div className="bg-white rounded-2xl shadow-coffee-lg p-6 space-y-4">
          <Skeleton width="160px" height="20px" />
          <Skeleton width="100%" height="40px" />
          <Skeleton width="100%" height="40px" />
          <Skeleton width="100%" height="40px" />
          <Skeleton width="120px" height="36px" />
        </div>
      </Shell>
    );
  }

  if (error || !card || !link) {
    return (
      <Shell username={username}>
        <div className="bg-white rounded-2xl shadow-coffee-lg p-8 text-center">
          <p className="font-mono text-xs uppercase tracking-wider text-coffee-500 mb-3">404</p>
          <h1 className="text-2xl font-bold text-coffee-900 mb-3">Form not found</h1>
          <p className="text-sm text-coffee-600">{error || "This form has been removed."}</p>
        </div>
      </Shell>
    );
  }

  const buttonLabel = link.formConfig?.buttonLabel || link.title || "Contact me";
  const description = link.formConfig?.description;
  const customFields = link.formConfig?.customFields || [];

  const inputClass = "w-full h-11 px-3 rounded border border-coffee-200 bg-white text-sm text-coffee-900 placeholder-coffee-400 focus:border-coffee-600 focus:outline-none";
  const labelClass = "block text-xs font-semibold text-coffee-700 mb-1";

  if (state === "sent") {
    return (
      <Shell username={username}>
        <div className="bg-white rounded-2xl shadow-coffee-lg p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-success-50 mx-auto mb-4 flex items-center justify-center">
            <Check className="w-6 h-6 text-success-700" />
          </div>
          <h1 className="text-xl font-bold text-coffee-900 mb-2">Got it.</h1>
          <p className="text-sm text-coffee-600 mb-6">{card.name} will reply soon.</p>
          <Link
            to={`/card/${card.username}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-coffee-900 text-cream-50 rounded text-sm font-medium hover:bg-coffee-800"
          >
            <ArrowLeft className="w-3 h-3" /> Back to {card.name.split(" ")[0]}'s card
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell username={username}>
      <div className="bg-white rounded-2xl shadow-coffee-lg p-6">
        {/* Mini-byline so the visitor knows whose form they're filling */}
        <div className="flex items-center gap-3 pb-4 mb-5 border-b border-coffee-100">
          <div className="w-10 h-10 rounded-full bg-coffee-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {card.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-coffee-900 truncate">{card.name}</p>
            <p className="text-xs text-coffee-600 truncate">
              {card.position}
              {card.company ? ` · ${card.company}` : ""}
            </p>
          </div>
        </div>

        <h1 className="text-xl font-bold text-coffee-900 mb-1">{buttonLabel}</h1>
        {description && <p className="text-sm text-coffee-600 mb-5">{description}</p>}
        {!description && <div className="mb-5" />}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Name<span className="text-accent-500"> *</span></label>
            <input
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder="Your name"
            />
          </div>
          <div>
            <label className={labelClass}>Phone<span className="text-accent-500"> *</span></label>
            <input
              required
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputClass}
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label className={labelClass}>Email<span className="text-accent-500"> *</span></label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="you@company.com"
            />
          </div>
          {customFields.map((field) => (
            <CustomField
              key={field.id}
              field={field}
              value={extra[field.id]}
              onChange={(v) => setExtra((prev) => ({ ...prev, [field.id]: v }))}
              inputClass={inputClass}
              labelClass={labelClass}
              onFileSelected={handleFileSelected}
              attachmentName={attachment?.filename}
              attachmentError={attachmentError}
            />
          ))}
          <button
            type="submit"
            disabled={state === "submitting"}
            className="w-full h-11 bg-coffee-900 text-cream-50 rounded font-medium hover:bg-coffee-800 transition-colors disabled:opacity-50"
          >
            {state === "submitting" ? "Sending…" : "Send"}
          </button>
          {state === "error" && (
            <p className="text-sm text-danger-500 text-center">Something went wrong. Try again?</p>
          )}
        </form>
      </div>
    </Shell>
  );
};

export default PublicCardForm;
